import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchSkinOffer, startTryOn, tryOnForSong } from '../_helpers/skinTryOn.js';

/**
 * Trying a skin on (Skins, mockup 36, ENG-737). Local state only; nobody
 * else's screen changes.
 *
 * `songKey` is the playing song (`playingSongKey`). `start(skin)` tries a
 * skin on (a normalized `SkinsSkin`) and reads its offer for the panel's
 * "Get it"; `takeOff()` is "Take it off". A try-on also ends when `songKey`
 * changes. `refresh(skinId?)` reads the offer of the skin on again (after a
 * price-changed 409, ENG-827). Returns `{ tryOn, offer, start, takeOff, refresh }`: `tryOn` is
 * `{ skin, look, paint, songKey }` or null, `offer` is
 * `{ status: 'loading' | 'ready' | 'error', offer }` for the skin on.
 */
export const useSkinTryOn = (songKey) => {
  const [tryOn, setTryOn] = useState(null);
  const [offer, setOffer] = useState({ status: 'loading', offer: null });
  const songKeyRef = useRef(songKey ?? null);
  songKeyRef.current = songKey ?? null;
  // Which try-on the offer being read belongs to, so a late answer is dropped.
  const startedRef = useRef(0);
  // The try-on on at the last render, for refresh().
  const tryOnRef = useRef(null);

  const start = useCallback((skin) => {
    const next = startTryOn(skin, songKeyRef.current);
    startedRef.current += 1;
    setTryOn(next);
    if (!next) return false;
    const started = startedRef.current;
    setOffer({ status: 'loading', offer: null });
    fetchSkinOffer(next.skin.id)
      .then((answer) => { if (startedRef.current === started) setOffer({ status: 'ready', offer: answer }); })
      .catch(() => { if (startedRef.current === started) setOffer({ status: 'error', offer: null }); });
    return true;
  }, []);

  // Reads the offer of the skin on again (its price changed, ENG-827). Get it
  // hides until the new offer is in, so the next tap sends the new price.
  const refresh = useCallback((skinId) => {
    const current = tryOnRef.current;
    if (!current || (skinId && current.skin.id !== skinId)) return;
    startedRef.current += 1;
    const started = startedRef.current;
    setOffer({ status: 'loading', offer: null });
    fetchSkinOffer(current.skin.id)
      .then((answer) => { if (startedRef.current === started) setOffer({ status: 'ready', offer: answer }); })
      .catch(() => { if (startedRef.current === started) setOffer({ status: 'error', offer: null }); });
  }, []);

  const takeOff = useCallback(() => {
    startedRef.current += 1;
    setTryOn(null);
  }, []);

  // A try-on from an earlier song is over, so it can't come back.
  useEffect(() => {
    setTryOn((current) => tryOnForSong(current, songKey));
  }, [songKey]);

  // Checked at render too, so the skin never draws for the next song.
  const current = tryOnForSong(tryOn, songKey);
  tryOnRef.current = current;
  return { tryOn: current, offer, start, takeOff, refresh };
};

export default useSkinTryOn;
